/**
 * The review queue (docs/ux/casefile.md): a 400 px listbox of the machine's
 * items in its stable order, then the additional corrections and the picker
 * for a document the machine did not flag. Two-line 56 px rows: the decision
 * word, the category and the page range; the manifest title and the
 * escalation-worthy note. Arrows move and show, Enter hands focus to the
 * detail, Home and End jump. The word always carries the state.
 */

import { cn } from 'cn';
import { CheckIcon, CircleIcon, FlagIcon, MinusCircleIcon, PenLineIcon } from 'lucide-react';
import { useEffect, useRef, type KeyboardEvent } from 'react';
import type { CasefileManifest, SortReviewItem } from '@/app/types';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CATEGORY_LABEL, SECTIONS, confidenceText, decisionWord, docLine, isEscalationWorthy, itemDoc, pagesText, type DecisionWord } from './describe';

const MARK: Record<DecisionWord, { icon: typeof CheckIcon; tone: string }> = {
  Undecided: { icon: CircleIcon, tone: 'text-beam-text-muted' },
  Accepted: { icon: CheckIcon, tone: 'text-beam-success' },
  Corrected: { icon: PenLineIcon, tone: 'text-foreground' },
  Escalated: { icon: FlagIcon, tone: 'text-beam-warning' },
  Withdrawn: { icon: CircleIcon, tone: 'text-beam-text-muted' },
  Unresolved: { icon: CircleIcon, tone: 'text-beam-text-muted' },
  Missing: { icon: MinusCircleIcon, tone: 'text-beam-text-muted' },
  Flagged: { icon: FlagIcon, tone: 'text-beam-warning' },
};

export function DecisionMark({ word, size = 'row' }: { word: DecisionWord; size?: 'row' | 'table' }) {
  const { icon: Icon, tone } = MARK[word];
  return (
    <span data-slot="decision-mark" data-decision={word.toLowerCase()} className={cn('inline-flex items-center gap-1 font-medium', tone, size === 'row' ? 'text-xs' : 'text-[13px]')}>
      <Icon aria-hidden="true" className="size-3.5" />
      {word}
    </span>
  );
}

export type QueueProps = {
  matterRef: string;
  items: SortReviewItem[];
  manifest: CasefileManifest;
  /** The working manifest (machine plus saved changes), for the picker, so a document a split created can be changed in its turn. */
  working: CasefileManifest;
  escalationConfidence: number;
  selectedId: string | null;
  blockingOnly: boolean;
  locked: boolean;
  onSelect: (itemId: string) => void;
  onOpen: () => void;
  onToggleBlocking: () => void;
  onPickDocument: (documentId: string) => void;
  pickedDocumentId: string | null;
};

export function rowName(item: SortReviewItem, manifest: CasefileManifest, threshold: number): string {
  const doc = itemDoc(item, manifest);
  return [decisionWord(item), CATEGORY_LABEL[item.category], pagesText(item.startPage, item.endPage).toLowerCase(), doc?.title ?? '', isEscalationWorthy(item, threshold) ? 'escalation-worthy' : ''].filter(Boolean).join(', ');
}

function Row({ item, manifest, threshold, selected, focusable, onSelect, onKeyDown }: { item: SortReviewItem; manifest: CasefileManifest; threshold: number; selected: boolean; focusable: boolean; onSelect: () => void; onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void }) {
  const doc = itemDoc(item, manifest);
  const worthy = isEscalationWorthy(item, threshold);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (selected && focusable && document.activeElement?.closest('[role="listbox"]')) ref.current?.focus(); }, [selected, focusable]);
  return (
    <div
      ref={ref}
      role="option"
      aria-selected={selected}
      aria-current={selected ? 'true' : undefined}
      aria-label={rowName(item, manifest, threshold)}
      tabIndex={focusable ? 0 : -1}
      data-item-id={item.itemId}
      data-category={item.category}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={cn(
        'flex h-14 cursor-pointer flex-col justify-center gap-0.5 border-l-2 px-4 outline-none hover:bg-beam-cream-deep focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:ring-inset',
        selected ? 'border-l-beam-signature bg-beam-cream-deep' : 'border-l-transparent',
      )}
    >
      <div className="flex items-center gap-2 text-[13px]">
        <DecisionMark word={decisionWord(item)} />
        <Tooltip>
          <TooltipTrigger asChild><span className={cn(selected && 'font-medium')}>{CATEGORY_LABEL[item.category]}</span></TooltipTrigger>
          <TooltipContent>{item.category}</TooltipContent>
        </Tooltip>
        <span className="tabular ml-auto text-beam-text-muted">{pagesText(item.startPage, item.endPage)}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-beam-text-muted">
        {doc ? (
          <Tooltip>
            <TooltipTrigger asChild><span className="min-w-0 flex-1 truncate">{doc.title}</span></TooltipTrigger>
            <TooltipContent className="max-w-xs">{docLine(doc)}</TooltipContent>
          </Tooltip>
        ) : <span className="flex-1" />}
        {worthy ? <span className="shrink-0 font-medium text-beam-warning">Escalation-worthy</span> : null}
      </div>
    </div>
  );
}

export function AdditionalCorrectionPicker({ manifest, value, onPick, disabled }: { manifest: CasefileManifest; value: string | null; onPick: (documentId: string) => void; disabled: boolean }) {
  // Every document but a copy is offered, and a document may take more than
  // one change (a split, then a section for one half), so nothing else is
  // excluded; the list reads from the working manifest, so it carries the
  // documents earlier changes created (D-0081).
  const groups = SECTIONS.filter((s) => s !== 'Duplicates').map((section) => ({ section, docs: manifest.documents.filter((d) => d.section === section && !d.duplicateOf) })).filter((g) => g.docs.length > 0);
  return (
    <Select value={value ?? ''} onValueChange={onPick} disabled={disabled}>
      <SelectTrigger aria-label="Change another document" className="h-9 w-full text-[13px]">
        <SelectValue placeholder="Change another document" />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {groups.map((g) => (
          <SelectGroup key={g.section}>
            <SelectLabel>{g.section}</SelectLabel>
            {g.docs.map((d) => <SelectItem key={d.id} value={d.id} className="text-[13px]">{docLine(d)}</SelectItem>)}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ReviewQueue(props: QueueProps) {
  const { matterRef, items, manifest, working, escalationConfidence, selectedId, blockingOnly, locked, onSelect, onOpen, onToggleBlocking, onPickDocument, pickedDocumentId } = props;
  const flagged = items.filter((i) => i.category !== 'additional');
  const additional = items.filter((i) => i.category === 'additional');
  const blockingCount = items.filter((i) => i.decision === 'unresolved' || i.decision === 'escalated').length;
  const shown = blockingOnly ? flagged.filter((i) => i.decision === 'unresolved' || i.decision === 'escalated') : flagged;
  const all = [...shown, ...(blockingOnly ? [] : additional)];
  const focusId = selectedId && all.some((i) => i.itemId === selectedId) ? selectedId : all[0]?.itemId ?? null;

  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    const index = all.findIndex((i) => i.itemId === (selectedId ?? focusId));
    const move = (to: number) => { const target = all[Math.max(0, Math.min(all.length - 1, to))]; if (target) { e.preventDefault(); onSelect(target.itemId); } };
    if (e.key === 'ArrowDown') move(index + 1);
    else if (e.key === 'ArrowUp') move(index - 1);
    else if (e.key === 'Home') move(0);
    else if (e.key === 'End') move(all.length - 1);
    else if (e.key === 'Enter') { e.preventDefault(); onOpen(); }
  }

  return (
    <section aria-label="Review items" data-slot="review-queue" className="flex flex-col overflow-hidden rounded-xl border border-beam-line bg-beam-paper">
      <header className="flex h-10 items-center justify-between gap-3 px-4">
        <h2 className="eyebrow text-beam-text-muted">Review items <span className="tabular">{flagged.length}</span></h2>
        {locked ? null : <button
          type="button"
          aria-pressed={blockingOnly}
          title="Show blocking items only"
          onClick={onToggleBlocking}
          className={cn('inline-flex h-7 items-center gap-1 rounded-full border border-beam-line px-2.5 text-xs font-medium hover:bg-beam-cream-deep', blockingOnly && 'bg-beam-cream-deep')}
        >
          Blocking <span className="tabular">{blockingCount}</span>
        </button>}
      </header>
      {flagged.length === 0 ? (
        <p className="px-4 pb-4 text-[13px] text-beam-text-muted">The sorter raised no review items for this run.</p>
      ) : shown.length === 0 ? (
        <div className="flex flex-col items-start gap-2 px-4 pb-4 text-[13px]">
          <p className="text-beam-text-muted">No items block completion.</p>
          <button type="button" className="text-beam-signature underline-offset-4 hover:underline" onClick={onToggleBlocking}>Show all</button>
        </div>
      ) : null}
      {/* Two listboxes, so the "Additional corrections" heading never sits between options (weekly review, 9 September 2026); the arrow keys still walk both. */}
      <div role="listbox" aria-label={`Review items for ${matterRef}`} className="flex flex-col divide-y divide-beam-line border-t border-beam-line">
        {shown.map((item) => (
          <Row key={item.itemId} item={item} manifest={manifest} threshold={escalationConfidence} selected={item.itemId === selectedId} focusable={item.itemId === focusId} onSelect={() => onSelect(item.itemId)} onKeyDown={onKey} />
        ))}
      </div>
      {!blockingOnly ? (
        <>
          <div className="flex h-10 items-center border-t border-beam-line px-4"><h3 id={`additional-${matterRef}`} className="eyebrow text-beam-text-muted">Additional corrections <span className="tabular">{additional.length}</span></h3></div>
          <div role="listbox" aria-labelledby={`additional-${matterRef}`} className="flex flex-col divide-y divide-beam-line border-t border-beam-line">
            {additional.map((item) => (
              <Row key={item.itemId} item={item} manifest={manifest} threshold={escalationConfidence} selected={item.itemId === selectedId} focusable={item.itemId === focusId} onSelect={() => onSelect(item.itemId)} onKeyDown={onKey} />
            ))}
          </div>
        </>
      ) : null}
      {!blockingOnly && !locked ? (
        <div className="p-3">
          <AdditionalCorrectionPicker manifest={working} value={pickedDocumentId} onPick={onPickDocument} disabled={locked} />
        </div>
      ) : null}
    </section>
  );
}

export function escalationNote(item: SortReviewItem, threshold: number): string | null {
  return isEscalationWorthy(item, threshold) ? `Beam's threshold for escalation is a confidence under ${threshold}; this item is at ${confidenceText(item.confidence)}.` : null;
}
